import { useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import { useActiveLab, useOrder } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useToggle } from '@/hooks/useToggle';
import { useTab } from '@/hooks/useTab';
import { CloseIcon, OptionsIcon } from '@/icons';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import EditLabOrderDialog from '@/dialogs/EditLabOrderDialog';
import DeleteOrderDialog from '@/dialogs/DeleteOrderDialog';
import TabsBlock, { type TabChild } from '@/components/TabsBlock';
import OverviewTab from '../OverviewTab';
import type { OrderOverlayTab } from '../types';
import { classes, getDefaultTab } from './utils';

export type TabsColumnProps = {
  isFetchingData: boolean;
};

export default function TabsColumn({ isFetchingData }: TabsColumnProps) {
  const menuToggleRef = useRef(null);
  const { activeOrderId, onClearActiveOrder } = useAppState();
  const { searchParams, pushParams } = usePath();
  const activeLab = useActiveLab();
  const order = useOrder(activeOrderId);
  const [defaultTab, setDefaultTab] = useTab<OrderOverlayTab>(
    getDefaultTab(searchParams)
  );
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isOrderDialogOpen, toggleOrderDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();

  useEffect(() => {
    const tab = searchParams.get('orderOverlayTab');
    if (tab !== null && tab !== defaultTab) {
      handleSelectTab(tab as OrderOverlayTab);
    }
  }, [searchParams]);

  const handleSelectTab = (tab: OrderOverlayTab) => {
    setDefaultTab(tab);
    pushParams({ orderOverlayTab: tab });
  };

  if (!order) return null;

  let tabs: TabChild[] = [
    <OverviewTab
      key="overview"
      isFetchingData={isFetchingData}
      name="overview"
      label="Overview"
    />,
  ];

  return (
    <>
      {isOrderDialogOpen && (
        <EditLabOrderDialog
          orderId={order.id}
          onClose={() => toggleOrderDialog(false)}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteOrderDialog
          orderId={order.id}
          onDelete={onClearActiveOrder}
          onClose={() => toggleDeleteDialog(false)}
        />
      )}
      <div className={classes.wrapper}>
        <div className={classes.buttonsWrapper}>
          {activeLab && (
            <>
              <IconButton
                ref={menuToggleRef}
                aria-label="options"
                onClick={() => toggleMenu()}>
                <OptionsIcon className={classes.icon} />
              </IconButton>
              <DefaultOptionsMenu
                anchorTo={menuToggleRef.current}
                open={isMenuOpen}
                onEdit={() => toggleOrderDialog(true)}
                onDelete={() => toggleDeleteDialog(true)}
                onClose={() => toggleMenu()}
              />
            </>
          )}
          <IconButton aria-label="close" onClick={onClearActiveOrder}>
            <CloseIcon className={classes.icon} />
          </IconButton>
        </div>
        <TabsBlock<OrderOverlayTab[]>
          className={classes.tabsBlock}
          name="order-info"
          bodyClassName={classes.tabBody}
          defaultTab={defaultTab}
          onChange={handleSelectTab}>
          {tabs}
        </TabsBlock>
      </div>
    </>
  );
}
